import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";

import { uriTransformer } from "./custom-functions/UriTransformer";
import { DropdownContext } from "./contexts/DropdownContext";
import { useStatement } from "./contexts/StatementContext";
import { getQuestion } from "../data-sources/api";

/**
 * SideBarMenu component that manages and displays the sidebar menu with dropdowns.
 * 
 * @param {Array} menuChildren - The children menu items to display.
 * @param {number} menuId - The ID of the menu.
 * @returns {JSX.Element} The rendered sidebar menu with dropdowns.
 */
export const SideBarMenu = ({ menuChildren, menuId }) => {
  const [openDropdowns, setOpenDropdowns] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Routes to main section on page reload
   */
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 1) {
      const newPath = `/${pathSegments[0]}`;
      navigate(newPath, { replace: true });
    }
  }, []);

  return (
    <DropdownContext.Provider value={{ openDropdowns, setOpenDropdowns }}>
      <nav className="sidebar-nav">
        <Dropdown
          menuChildren={menuChildren}
          menuId={menuId}
          customClass={`sidebar-items medium-text `}
        />
      </nav>
    </DropdownContext.Provider>
  );
}

/**
 * Dropdown component that manages and displays a dropdown menu in the sidebar menu.
 * 
 * @param {Array} menuChildren - The children menu items to display.
 * @param {number} menuId - The ID of the menu.
 * @param {string} customClass - Custom CSS class for styling.
 * @returns {JSX.Element} The rendered dropdown menu.
 */
export const Dropdown = ({ menuChildren, menuId, customClass }) => {
  const { openDropdowns, setOpenDropdowns } = React.useContext(DropdownContext);

  // Toggle the dropdown menu open or closed
  const toggleDropdown = (menuText, childId) => {
    const currentDropdownMenu = `menu_${menuId}`;
    const dropdownKey = `${menuId}-${uriTransformer(menuText)}_${childId}`;
    const isOpen = openDropdowns[currentDropdownMenu] ? openDropdowns[currentDropdownMenu][dropdownKey] : false;

    // Open the current dropdown, and if navigating to a top-level dropdown close the submenu dropdowns
    setOpenDropdowns((prevDropdowns) => {
      const entries = Object.entries(prevDropdowns);
      const firstEntry = entries.length > 0 ? { [entries[0][0]]: entries[0][1] } : {};

      return {
        ...firstEntry,
        [currentDropdownMenu]: {
          [dropdownKey]: !isOpen, // Invert the current state
        }
      };
    });
  };

  return (
    <ul>
      {menuChildren.map((childItem) => {
        const currentDropdownMenu = `menu_${menuId}`;
        const isOpen = openDropdowns[currentDropdownMenu] ? openDropdowns[currentDropdownMenu][`${menuId}-${uriTransformer(childItem.menuText)}_${childItem.id}`] : false;

        return (
          <React.Fragment key={childItem.id}>
            <NavLink
              className="link-style"
              onClick={() => { toggleDropdown(childItem.menuText, childItem.id) }}
              to={uriTransformer(childItem.menuText)}
            >
              <li className={`${customClass}`}>
                {childItem.menuText}
                <span className="toggle" role="button">
                  <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
                </span>
              </li>
            </NavLink>
            {isOpen && <Outlet />}
          </React.Fragment>
        );
      })}
    </ul>
  );
};

/**
 * StatementList component that fetches and displays a list of statements.
 * 
 * @param {number} subMenuId - The ID of the submenu to fetch statements for.
 * @returns {JSX.Element} The rendered list of statements.
 */
export const StatementList = ({ subMenuId }) => {
  const [statements, setStatements] = useState([]);
  const { setStatementID } = useStatement();

  /** 
   * Fetches statement data based on the submenu ID.
  */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getQuestion(subMenuId);
        setStatements(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [subMenuId]);

  return (
    <ul>
      {statements.map((statementItem) => (
        <li
          key={statementItem.id}
          className="sidebar-items dropdown-children light-text"
          onClick={() => {
            setStatementID(statementItem.id);
          }}
        >
          {statementItem.textUrl}
        </li>
      ))}
    </ul>
  );
};